import React, { useState, useEffect } from 'react';

const CardHolders = () => {
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showFullData, setShowFullData] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    cardType: 'visa',
    pricePoint: 6.99
  });
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchCardHolders();
    fetchActiveJobs();
    const interval1 = setInterval(fetchCardHolders, 5000);
    const interval2 = setInterval(fetchActiveJobs, 3000);
    return () => {
      clearInterval(interval1);
      clearInterval(interval2);
    };
  }, []);

  const fetchActiveJobs = async () => {
    try {
      const response = await fetch('/api/cardholder-jobs', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setActiveJobs(data.filter(j => j.status === 'running'));
      }
    } catch (err) {
      console.error('Error fetching active jobs:', err);
    }
  };

  const stopJob = async (jobId) => {
    try {
      const response = await fetch(`/api/cardholder-jobs/${jobId}/stop`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        alert('Job stopped successfully');
        fetchActiveJobs();
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const syncFromBluebanc = async () => {
    if (!confirm('Sync all cardholders from Bluebanc API? This may take a moment.')) return;

    setSyncing(true);
    try {
      const response = await fetch('/api/sync-bluebanc/cardholders', {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error);
      }

      const data = await response.json();
      alert(`Sync completed!\n${data.added} new cardholders added\n${data.skipped} duplicates skipped`);
      fetchCardHolders();
    } catch (err) {
      alert(`Sync error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const fetchCardHolders = async () => {
    try {
      const response = await fetch('/api/cardholders', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCardholders(data);
      }
    } catch (err) {
      console.error('Error fetching cardholders:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateCustomer = async () => {
    try {
      const response = await fetch('/api/cardholders/generate-customer', {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to generate customer');
      
      const fullClient = await response.json();
      
      setFormData({ 
        ...formData, 
        firstName: fullClient.firstName,
        lastName: fullClient.lastName,
        email: fullClient.email,
        phone: fullClient.phone,
        address: fullClient.address,
        city: fullClient.city,
        state: fullClient.state,
        zipCode: fullClient.zip || fullClient.zipCode
      });
    } catch (err) {
      console.error('Error generating customer:', err);
      alert('Error generating customer data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      console.log('📤 Submitting customer data:', formData);
      
      const response = await fetch('/api/cardholders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error);
      }

      alert('CardHolder created successfully!');
      setShowForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        cardType: 'visa',
        pricePoint: 6.99
      });
      fetchCardHolders();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const maskCard = (cardNumber) => {
    if (!cardNumber || cardNumber.length < 4) return '****';
    return `****-****-****-${cardNumber.slice(-4)}`;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {activeJobs.length > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-bold text-gray-900 mb-4">🔄 Active CardHolder Creation Jobs</h3>
          {activeJobs.map(job => (
            <div key={job.id} className="bg-white rounded p-4 mb-3 last:mb-0">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">{job.name}</span>
                <button
                  onClick={() => stopJob(job.id)}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  ⏹ Stop
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Progress:</span>
                  <span className="font-medium">{job.createdCards}/{job.totalCards} ({job.progress.toFixed(1)}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${job.progress}%` }}
                  ></div>
                </div>
                {job.failedCards > 0 && (
                  <div className="text-sm text-red-600">Failed: {job.failedCards}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {showFullData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">CardHolder Full Data</h3>
              <button onClick={() => setShowFullData(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-gray-600">Name:</span> <span className="font-medium">{showFullData.firstName} {showFullData.lastName}</span></div>
                <div><span className="text-gray-600">Email:</span> <span className="font-medium">{showFullData.email}</span></div>
              </div>
              <div><span className="text-gray-600">Phone:</span> <span className="font-medium">{showFullData.phone}</span></div>
              <div><span className="text-gray-600">Address:</span> <span className="font-medium">{showFullData.address}, {showFullData.city}, {showFullData.state} {showFullData.zipCode}</span></div>
              <div className="border-t pt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div><span className="text-gray-600">Card Type:</span> <span className="font-medium">{showFullData.cardType.toUpperCase()}</span></div>
                  <div><span className="text-gray-600">Price Point:</span> <span className="font-medium">${showFullData.pricePoint}</span></div>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded">
                <p className="font-medium text-gray-900 mb-2">🔐 Sensitive Card Data:</p>
                <div className="space-y-2">
                  <div><span className="text-gray-600">Full Card Number:</span> <span className="font-mono font-bold">{showFullData.cardNumber}</span></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><span className="text-gray-600">CVV:</span> <span className="font-mono font-bold">{showFullData.cvv || 'N/A'}</span></div>
                    <div><span className="text-gray-600">Expiry:</span> <span className="font-mono font-bold">{showFullData.expiryMonth}/{showFullData.expiryYear}</span></div>
                  </div>
                </div>
              </div>
              <div className="border-t pt-3">
                <div><span className="text-gray-600">USA IP Address:</span> <span className="font-mono font-medium">{showFullData.ipAddress || 'N/A'}</span></div>
                <div className="text-xs text-gray-500 mt-2">
                  <div>Bluebanc Cardholder ID: {showFullData.bluebancHolderId}</div>
                  <div>Bluebanc Card ID: {showFullData.bluebancCardId}</div>
                </div>
              </div>
              {showFullData.usedGateways && JSON.parse(showFullData.usedGateways).length > 0 && (
                <div className="border-t pt-3">
                  <p className="font-medium text-gray-900 mb-2">Used on Gateways:</p>
                  <div className="space-y-1 text-xs">
                    {JSON.parse(showFullData.usedGateways).map((ug, idx) => (
                      <div key={idx} className="bg-gray-50 p-2 rounded">
                        <div className="font-medium">{ug.gatewayName}</div>
                        <div className="text-gray-600">Processor: {ug.processor} • Bank: {ug.bank}</div>
                        <div className="text-gray-500">{new Date(ug.timestamp).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">CardHolders</h2>
          <p className="text-gray-600 mt-2">
            Manage Bluebanc cardholders and cards
            {cardholders.length > 0 && (
              <span className="ml-2 text-primary-600 font-medium">
                • Total: {cardholders.length}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={syncFromBluebanc}
            disabled={syncing}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {syncing ? '⏳ Syncing...' : '🔄 Sync From Bluebanc API'}
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700"
          >
            {showForm ? 'Cancel' : '+ Create CardHolder'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-bold mb-4">Create New CardHolder</h3>
          <button
            onClick={generateCustomer}
            className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            🎲 Generate Random Customer
          </button>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="tel"
              placeholder="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="text"
              placeholder="Address"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="City"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="State"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                required
                maxLength="2"
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="Zip Code"
                value={formData.zipCode}
                onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <select
                value={formData.cardType}
                onChange={(e) => setFormData({...formData, cardType: e.target.value})}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="visa">VISA</option>
                <option value="mastercard">Mastercard</option>
              </select>
              <input
                type="number"
                step="0.01"
                placeholder="Price Point"
                value={formData.pricePoint}
                onChange={(e) => setFormData({...formData, pricePoint: e.target.value})}
                required
                className="px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <button
              type="submit"
              disabled={creating}
              className="w-full px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {creating ? '⏳ Creating...' : 'Create CardHolder'}
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading cardholders...</p>
        </div>
      ) : cardholders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No cardholders created yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Card</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Used On</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {cardholders.map((ch) => {
                const usedGateways = ch.usedGateways ? JSON.parse(ch.usedGateways) : [];
                return (
                  <tr key={ch.id}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {ch.firstName} {ch.lastName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{ch.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{ch.phone}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {ch.address}, {ch.city}, {ch.state} {ch.zipCode}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        ch.cardType === 'visa' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {ch.cardType.toUpperCase()}
                      </span>
                      <p className="text-xs text-gray-500 mt-1 font-mono">{maskCard(ch.cardNumber)}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">${ch.pricePoint}</td>
                  <td className="px-6 py-4 text-sm">
                    {usedGateways.length > 0 ? (
                      <span className="text-xs text-gray-600">{usedGateways.length} gateway(s)</span>
                    ) : (
                      <span className="text-xs text-green-600">Unused</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    <button
                      onClick={() => setShowFullData(ch)}
                      className="text-primary-600 hover:text-primary-900 font-medium"
                    >
                      Show Full Data
                    </button>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CardHolders;
